import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  isSameMonth, 
  isSameDay, 
  addDays, 
  isWithinInterval,
  isBefore,
  parseISO,
  isWeekend,
  getMonth
} from 'date-fns';
import { es, enUS, ptBR } from 'date-fns/locale';
import { initMercadoPago, Wallet } from '@mercadopago/sdk-react';
import InputField from './form/InputField';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiCalendar, FiShield, FiWind, FiTarget, 
  FiTrendingDown, FiCheckCircle, FiAlertCircle, FiArrowLeft, 
  FiRefreshCw, FiChevronLeft, FiChevronRight, FiLoader,
  FiMessageCircle, FiSend, FiCreditCard, FiSmartphone, FiCopy,
  FiDownload, FiInfo, FiTag, FiShoppingBag, FiStar
} = FiIcons;

initMercadoPago('APP_USR-8a67f66b-6963-4e5b-b0af-0f068f7f5cf8');

// BASE RATES PER GUEST COUNT
const BASE_RATE_1_GUEST = 115000;
const BASE_RATE_2_GUESTS = 130000;
const BASE_RATE_3_GUESTS = 145000;

const WEEKEND_SURCHARGE = 0.20;
const SEASONAL_MULTIPLIER = { 9: 1.15, 10: 1.15, 11: 1.25 };
const INSURANCE_RATE = 23000;
const CLEANING_FEE_FIXED = 65000;
const PET_FEE_PER_NIGHT = 35000;
const SERVICE_FEE_RATE = 0.05;
const CARD_SURCHARGE_RATE = 0.04;

const NEQUI_QR_URL = "https://media-manager-c.questera.ai/greta-media/22aa865ee86cefe9c860e7d06bbd1b00a5df93e170adbe6f023ba9046dde80b0080d4241ae2bf2b28b7ae4a2b0aac3e9/images/aW1hZ2UvanBlZw==/38688a95b428f327a41ea8ae4dce2aa2.jpeg";
const NEQUI_NUMBER = "3007719418";

function PricingBreakdown({ pricing, paymentType, t }) {
  if (!pricing || !pricing.isValid) return null;

  const formatCOP = (val) => 
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val);

  return (
    <motion.div 
      initial={{ opacity: 0, height: 0 }} 
      animate={{ opacity: 1, height: 'auto' }}
      className="bg-gray-50 rounded-3xl overflow-hidden border border-gray-100"
    >
      <div className="p-6 space-y-4">
        <h4 className="text-xs font-black uppercase tracking-widest text-gray-400 flex items-center gap-2">
          <SafeIcon icon={FiInfo} /> {t.priceBreakdown}
        </h4>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500 font-medium">{t.stay} ({pricing.nights} {t.nights})</span>
            <span className="text-gray-900 font-bold">{formatCOP(pricing.baseSubtotal)}</span>
          </div>
          
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500 font-medium">{t.insurance}</span>
            <span className="text-gray-900 font-bold">{formatCOP(pricing.insuranceTotal)}</span>
          </div>

          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500 font-medium">{t.cleaning}</span>
            <span className="text-gray-900 font-bold">{formatCOP(pricing.cleaningFeeTotal)}</span>
          </div>

          {pricing.petFeeTotal > 0 && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500 font-medium">{t.petFee}</span>
              <span className="text-gray-900 font-bold">{formatCOP(pricing.petFeeTotal)}</span>
            </div>
          )}

          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500 font-medium">{t.serviceFee} (5%)</span>
            <span className="text-gray-900 font-bold">{formatCOP(pricing.serviceFee)}</span>
          </div>

          {pricing.discountAmount > 0 && (
            <div className="flex justify-between items-center text-emerald-600 bg-emerald-50 px-3 py-2 rounded-xl text-sm font-bold border border-emerald-100">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiTag} />
                <span>{t.discount} ({pricing.discountPercent}%)</span>
              </div>
              <span>-{formatCOP(pricing.discountAmount)}</span>
            </div>
          )}

          {paymentType === 'card' && (
            <div className="flex justify-between items-center text-blue-600 bg-blue-50 px-3 py-2 rounded-xl text-sm font-bold border border-blue-100">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCreditCard} />
                <span>{t.cardFee} (4%)</span>
              </div>
              <span>{formatCOP(pricing.cardFee)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-emerald-900 p-6 text-white flex justify-between items-center">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-1">{t.totalToPay}</p>
          <p className="text-3xl font-black">{formatCOP(pricing.total)}</p>
        </div>
        <div className="text-right">
          <div className="bg-white/10 px-3 py-1.5 rounded-full flex items-center gap-2 text-[10px] font-black uppercase tracking-wider mb-2">
            <SafeIcon icon={FiStar} className="text-yellow-400" /> {t.bestPrice}
          </div>
          <p className="text-emerald-300 text-[10px] font-bold">{t.savings}{formatCOP(pricing.total * 0.15)} {t.vsAirbnb}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function BookingView({ t, lang, onBack, onSubmit, isSubmitting, initialDates }) {
  const [formData, setFormData] = useState({
    checkIn: initialDates?.checkIn || '',
    checkOut: initialDates?.checkOut || '',
    checkInTime: '15:00',
    guests: 1,
    pets: false,
    whatsapp: '',
    fullName: '',
    nationality: '',
    specialRequests: '',
    paymentType: 'transfer'
  });
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [preferenceId, setPreferenceId] = useState(null);
  const [isGeneratingPreference, setIsGeneratingPreference] = useState(false);

  useEffect(() => {
    if (initialDates) {
      setFormData(prev => ({ ...prev, checkIn: initialDates.checkIn, checkOut: initialDates.checkOut }));
    }
  }, [initialDates]);

  const pricing = useMemo(() => {
    if (!formData.checkIn || !formData.checkOut) return null;
    const start = parseISO(formData.checkIn);
    const end = parseISO(formData.checkOut);
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (nights <= 0) return null;

    // Dynamic base rate selection based on occupancy
    let baseRateForOccupancy = BASE_RATE_1_GUEST;
    if (formData.guests === 2) baseRateForOccupancy = BASE_RATE_2_GUESTS;
    else if (formData.guests === 3) baseRateForOccupancy = BASE_RATE_3_GUESTS;

    let baseSubtotal = 0;
    for (let i = 0; i < nights; i++) {
      const current = addDays(start, i);
      const month = getMonth(current);
      let nightPrice = baseRateForOccupancy;
      
      // Apply seasonal multipliers
      if (SEASONAL_MULTIPLIER[month]) nightPrice *= SEASONAL_MULTIPLIER[month];
      
      // Apply weekend surcharges
      if (isWeekend(current)) nightPrice *= (1 + WEEKEND_SURCHARGE);
      
      baseSubtotal += nightPrice;
    }

    const insuranceTotal = nights * INSURANCE_RATE;
    const cleaningFeeTotal = CLEANING_FEE_FIXED;
    const petFeeTotal = formData.pets ? PET_FEE_PER_NIGHT * nights : 0;
    const serviceFee = baseSubtotal * SERVICE_FEE_RATE;

    let discountPercent = 0;
    if (nights >= 28) discountPercent = 0.25;
    else if (nights >= 7) discountPercent = 0.12;
    else if (nights >= 3) discountPercent = 0.05;

    const discountAmount = baseSubtotal * discountPercent;
    const subtotal = (baseSubtotal - discountAmount) + insuranceTotal + cleaningFeeTotal + petFeeTotal + serviceFee;
    const cardFee = formData.paymentType === 'card' ? subtotal * CARD_SURCHARGE_RATE : 0;
    const total = subtotal + cardFee;

    return {
      nights, baseSubtotal, insuranceTotal, cleaningFeeTotal, petFeeTotal, 
      serviceFee, discountPercent: (discountPercent * 100).toFixed(0),
      discountAmount, subtotal, cardFee, total, isValid: nights >= 2
    };
  }, [formData.checkIn, formData.checkOut, formData.guests, formData.pets, formData.paymentType]);

  const handleCreatePreference = async (currentPricing) => {
    if (!currentPricing) return;
    setIsGeneratingPreference(true);
    try {
      const res = await fetch('https://92afb.twidget.io/create-preference', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            title: `Reserva Loft Bogotá Apto 820 - ${currentPricing.nights} nights`,
            quantity: 1, unit_price: Math.round(currentPricing.total), currency_id: 'COP'
          }],
          back_urls: { success: window.location.href, failure: window.location.href, pending: window.location.href },
          auto_return: 'approved'
        })
      });
      const data = await res.json();
      if (data.data?.id || data.id) setPreferenceId(data.data?.id || data.id);
    } catch (e) { console.error(e); } finally { setIsGeneratingPreference(false); }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!pricing || !pricing.isValid) return;
    const success = await onSubmit({ ...formData, pricing });
    if (success) {
      setShowSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (formData.paymentType === 'card') handleCreatePreference(pricing);
    }
  };

  const formatCOP = (val) => 
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val);

  if (showSuccess) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white p-8 rounded-3xl shadow-2xl border border-emerald-100 text-center max-w-lg mx-auto">
        {formData.paymentType === 'transfer' ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-20 h-20 bg-emerald-100 text-emerald-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiSmartphone} className="text-4xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.qrTitle}</h2>
            <p className="text-gray-500 text-sm mb-6">{t.qrSubtitle}</p>
            <div className="bg-emerald-50 p-6 rounded-3xl mb-8 border-2 border-emerald-100">
              <p className="text-emerald-900 font-black text-2xl mb-6">{formatCOP(pricing.total)}</p>
              <img src={NEQUI_QR_URL} alt="Nequi QR" className="w-full aspect-square mx-auto rounded-2xl shadow-xl object-cover border-4 border-white mb-4" />
              <button onClick={() => {
                const link = document.createElement('a');
                link.href = NEQUI_QR_URL; link.download = 'QR.jpg';
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
              }} className="flex items-center gap-2 mx-auto mb-6 text-emerald-700 font-bold hover:bg-emerald-100 px-4 py-2 rounded-xl border border-emerald-200 bg-white">
                <SafeIcon icon={FiDownload} /> {t.downloadQR}
              </button>
              <div className="space-y-2 text-left bg-white p-4 rounded-xl border border-emerald-100">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{t.recipientDetails}</p>
                <div className="flex justify-between items-center text-sm font-bold text-gray-700">
                  <span>Nequi:</span>
                  <button onClick={() => navigator.clipboard.writeText(NEQUI_NUMBER)} className="flex items-center gap-2 text-emerald-700">
                    300 771 9418 <SafeIcon icon={FiCopy} className="text-xs" />
                  </button>
                </div>
              </div>
            </div>
            <button onClick={() => {
                const msg = `Hola Javier, reserva ${formData.checkIn} - ${formData.checkOut}. Total: ${formatCOP(pricing.total)}.`;
                window.open(`https://wa.me/573007719418?text=${encodeURIComponent(msg)}`, '_blank');
              }} className="w-full bg-emerald-800 text-white font-black py-4 rounded-2xl shadow-xl flex items-center justify-center gap-3">
              <SafeIcon icon={FiMessageCircle} /> {t.sendReceipt}
            </button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-20 h-20 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCreditCard} className="text-4xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.securePayment}</h2>
            <div className="bg-blue-50 p-8 rounded-3xl mb-8 border border-blue-100">
              <p className="text-blue-900 font-bold text-xl mb-4">{t.totalToPay}: {formatCOP(pricing.total)}</p>
              {isGeneratingPreference ? (
                <div className="py-8 flex flex-col items-center gap-3">
                  <SafeIcon icon={FiLoader} className="animate-spin text-4xl text-blue-600" />
                  <p className="text-sm font-bold text-blue-800">{t.initiatingGateway}</p>
                </div>
              ) : preferenceId && <Wallet initialization={{ preferenceId }} />}
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 pb-20">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-emerald-800 font-medium">
        <SafeIcon icon={FiArrowLeft} /> {t.back}
      </button>

      <div className="bg-white p-6 md:p-8 rounded-3xl shadow-xl border border-gray-100">
        <h2 className="text-2xl font-bold mb-6">{t.bookingTitle}</h2>
        
        <form onSubmit={handleFormSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField 
              label={t.checkIn} 
              name="checkIn" 
              type="date" 
              value={formData.checkIn} 
              onChange={(e) => setFormData(p => ({ ...p, checkIn: e.target.value }))} 
              min={format(new Date(), 'yyyy-MM-dd')}
              required 
            />
            <InputField 
              label={t.checkOut} 
              name="checkOut" 
              type="date" 
              value={formData.checkOut} 
              onChange={(e) => setFormData(p => ({ ...p, checkOut: e.target.value }))} 
              min={formData.checkIn || format(new Date(), 'yyyy-MM-dd')}
              required 
            />
            <InputField 
              label={t.fullName} 
              name="fullName" 
              value={formData.fullName} 
              onChange={(e) => setFormData(p => ({ ...p, fullName: e.target.value }))} 
              required 
            />
            <InputField 
              label={t.nationality} 
              name="nationality" 
              value={formData.nationality} 
              onChange={(e) => setFormData(p => ({ ...p, nationality: e.target.value }))} 
              required 
            />
            <InputField 
              label={t.whatsapp} 
              name="whatsapp" 
              type="tel" 
              value={formData.whatsapp} 
              onChange={(e) => setFormData(p => ({ ...p, whatsapp: e.target.value }))} 
              required 
            />
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">{t.guests}</label>
              <select name="guests" value={formData.guests} onChange={(e) => setFormData(p => ({ ...p, guests: parseInt(e.target.value) }))} className="w-full p-4 rounded-xl border-2 border-gray-100 bg-white font-medium">
                {[1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 flex items-center justify-between p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiTarget} className="text-emerald-700" />
                <div>
                  <p className="font-bold text-gray-900 text-sm">{t.petsQuestion}</p>
                  <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">{t.petsHelp}</p>
                </div>
              </div>
              <input type="checkbox" checked={formData.pets} onChange={(e) => setFormData(p => ({ ...p, pets: e.target.checked }))} className="w-6 h-6 accent-emerald-600" />
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-bold text-gray-700 flex items-center gap-2"><SafeIcon icon={FiShoppingBag} /> {t.paymentMethod}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button type="button" onClick={() => setFormData(p => ({ ...p, paymentType: 'transfer' }))} className={`p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-3 ${formData.paymentType === 'transfer' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100'}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${formData.paymentType === 'transfer' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-400'}`}><SafeIcon icon={FiSmartphone} /></div>
                <div><p className="text-sm font-bold text-gray-900">{t.transfer}</p><p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">{t.transferHelp}</p></div>
              </button>
              <button type="button" onClick={() => setFormData(p => ({ ...p, paymentType: 'card' }))} className={`p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-3 ${formData.paymentType === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-100'}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${formData.paymentType === 'card' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'}`}><SafeIcon icon={FiCreditCard} /></div>
                <div><p className="text-sm font-bold text-gray-900">{t.card}</p><p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">{t.cardHelp}</p></div>
              </button>
            </div>
          </div>

          {pricing && (
            <div className="space-y-4">
              {!pricing.isValid && <div className="bg-red-50 text-red-600 p-4 rounded-2xl text-sm font-bold border border-red-100">{t.minStay}</div>}
              <PricingBreakdown pricing={pricing} paymentType={formData.paymentType} t={t} />
            </div>
          )}

          <button type="submit" disabled={isSubmitting || !pricing || !pricing.isValid} className={`w-full font-black py-6 rounded-3xl shadow-2xl text-xl flex items-center justify-center gap-3 ${formData.paymentType === 'card' ? 'bg-blue-600' : 'bg-emerald-800'} text-white`}>
            {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={formData.paymentType === 'card' ? FiCreditCard : FiSend} /> {formData.paymentType === 'card' ? t.confirmPay : t.reserveNequi}</>}
          </button>
        </form>
      </div>
    </motion.div>
  );
}